import React, { useCallback, useRef, useState } from "react";
import { Button, CheckBox, DataChart } from "grommet";
import { FormNext } from "grommet-icons";

const GRAPH_WIDTH = 512;
const GRAPH_HEIGHT = 64;
const GRAPH_PADDING = 6;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getPrecision = (value) => {
  const decimalPart = value.toString().split(".")[1];
  return decimalPart ? decimalPart.length : 0;
};

const roundToStep = (value, minValue, stepValue) => {
  if (!stepValue) {
    return value;
  }

  const steppedValue =
    minValue + Math.round((value - minValue) / stepValue) * stepValue;

  return Number(steppedValue.toFixed(getPrecision(stepValue)));
};

const AutomationLane = ({
  enabled,
  isOpen,
  label,
  maxValue,
  minValue,
  onEnabledChange,
  onOpenChange,
  setValues,
  shiftStepPx = 5,
  shiftSteps = 0,
  stepValue,
  values,
}) => {
  const graphRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const valueRange = maxValue - minValue;
  const shiftPx = shiftSteps * shiftStepPx;
  const chartData = values.map((value, step) => ({ step, value }));

  const points = values.map((value, index) => {
    const normalizedX = values.length <= 1 ? 0 : index / (values.length - 1);
    const normalizedY =
      valueRange <= 0 ? 0 : clamp((value - minValue) / valueRange, 0, 1);
    const x = GRAPH_PADDING + normalizedX * (GRAPH_WIDTH - GRAPH_PADDING * 2);
    const y =
      GRAPH_PADDING +
      (1 - normalizedY) * (GRAPH_HEIGHT - GRAPH_PADDING * 2);

    return { x, y };
  });

  const updateValueFromPointer = useCallback(
    (event) => {
      if (!graphRef.current) {
        return;
      }

      const rect = graphRef.current.getBoundingClientRect();
      const localX = clamp(event.clientX - rect.left - shiftPx, 0, rect.width);
      const localY = clamp(event.clientY - rect.top, 0, rect.height);
      const stepIndex =
        values.length <= 1
          ? 0
          : Math.round((localX / rect.width) * (values.length - 1));
      const normalizedY = 1 - localY / rect.height;
      const rawValue = minValue + normalizedY * valueRange;
      const nextValue = clamp(
        roundToStep(rawValue, minValue, stepValue),
        minValue,
        maxValue,
      );

      setValues((currentValues) =>
        currentValues.map((value, index) =>
          index === stepIndex ? nextValue : value,
        ),
      );
    },
    [
      maxValue,
      minValue,
      setValues,
      shiftPx,
      stepValue,
      valueRange,
      values.length,
    ],
  );

  const handlePointerDown = useCallback(
    (event) => {
      setIsDragging(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
      updateValueFromPointer(event);
    },
    [updateValueFromPointer],
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (isDragging) {
        updateValueFromPointer(event);
      }
    },
    [isDragging, updateValueFromPointer],
  );

  const handlePointerUp = useCallback((event) => {
    setIsDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  return (
    <div className="automation-lane">
      <div className="automation-lane-inner">
        <div className="automation-lane-header">
          <Button
            a11yTitle={`Toggle ${label} automation lane`}
            aria-expanded={isOpen}
            className="automation-lane-toggle"
            icon={<FormNext color="rgba(255, 255, 255, 0.62)" />}
            label={`${label} automation`}
            onClick={() => onOpenChange(!isOpen)}
            plain
          />
          <div className="automation-enable">
            <CheckBox
              checked={enabled}
              label={<span className="automation-enable-label">On</span>}
              onChange={(event) => onEnabledChange(event.target.checked)}
              toggle
            />
          </div>
        </div>
        {isOpen && (
          <div
            className="automation-graph-window"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            ref={graphRef}
          >
            <div
              aria-hidden="true"
              className="automation-chart-backdrop"
              style={{ transform: `translateX(${shiftPx}px)` }}
            >
              <DataChart
                axis={false}
                bounds={{ y: [minValue, maxValue] }}
                chart={[
                  {
                    color: "brand",
                    opacity: "weak",
                    property: "value",
                    type: "area",
                  },
                  {
                    color: "brand",
                    opacity: "medium",
                    property: "value",
                    thickness: "hair",
                    type: "line",
                  },
                ]}
                data={chartData}
                guide={{
                  x: { granularity: "medium" },
                  y: { granularity: "medium" },
                }}
                pad="none"
                series={["step", "value"]}
                size={{ height: `${GRAPH_HEIGHT}px`, width: "fill" }}
              />
            </div>
            <svg
              aria-hidden="true"
              className="automation-graph"
              style={{ transform: `translateX(${shiftPx}px)` }}
              viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`}
            >
              <polyline
                className="automation-line"
                points={points.map(({ x, y }) => `${x},${y}`).join(" ")}
              />
              {points.map(({ x, y }, index) => (
                <circle
                  className="automation-node"
                  cx={x}
                  cy={y}
                  key={index}
                  r="3.4"
                />
              ))}
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(AutomationLane);
